import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import SessionContext from '../../../contexts/SessionContext';
import {
    fetchInvoicesFeedPage,
    invoicesFeedQueryKey,
    type InvoiceEntry,
} from '../../../hooks/invoice/useInvoicesFeed';
import { prefetchInvoiceDetail } from '../../../hooks/invoice/useGetInvoiceDetail';
import {
    invoicesListPageHasNext,
    invoicesSidebarTabFromViewState,
    INVOICES_LIST_FETCH_LIMIT,
    readInvoicesViewStateFromLocation,
    saveInvoicesViewStateCache,
    visibleInvoicesOnListPage,
    type InvoicesViewStateCache,
} from '../../../shared/invoicesListViewState';
import type { InvoiceFeedResponse } from '../../../types/invoice';
import {
    invoiceNeighborTooltip,
    neighborsFromFrozenPages,
    type InvoiceNeighborSource,
} from './invoiceDetailNeighbors';

export interface InvoiceDetailListNavigation {
    onPrevious: () => void;
    onNext: () => void;
    previousDisabled: boolean;
    nextDisabled: boolean;
    previousLoading: boolean;
    nextLoading: boolean;
    previousLabel: string;
    nextLabel: string;
}

interface FrozenQueue {
    key: string;
    pages: Record<number, InvoiceNeighborSource[]>;
    hasNext: Record<number, boolean>;
}

function readDetailSource(state: unknown): string | undefined {
    if (!state || typeof state !== 'object') return undefined;
    const source = (state as { source?: unknown }).source;
    return typeof source === 'string' ? source : undefined;
}

function snapshotFeedPage(invoices: readonly InvoiceEntry[] | undefined): {
    rows: InvoiceNeighborSource[];
    hasNext: boolean;
} {
    const fetched = invoices ?? [];
    return {
        rows: visibleInvoicesOnListPage(fetched).map((invoice) => ({
            _id: invoice._id,
            supplierText: invoice.supplierText,
            invoiceType: invoice.invoiceType,
            timestamp: invoice.timestamp,
            invoiceNumber: invoice.invoiceNumber,
            orderTotal: invoice.orderTotal,
        })),
        hasNext: invoicesListPageHasNext(fetched.length),
    };
}

function queueKeyFor(view: InvoicesViewStateCache): string {
    const locationIds = view.locationIds === 'all'
        ? 'all'
        : [...view.locationIds].sort().join(',');
    return JSON.stringify({
        type: view.type,
        showArchived: view.showArchived,
        locationIds,
        search: view.debouncedSearch,
        additionalFilters: view.additionalFilters,
    });
}

/**
 * Previous/next for an invoice opened from the invoicing list. The queue is
 * the filtered list from that visit. Archiving the open invoice does not
 * rebuild it; leaving for the feed or opening a differently filtered list does.
 */
export function useInvoiceDetailNeighbors(invoiceId: string | undefined): InvoiceDetailListNavigation | null {
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const sessionContext = useContext(SessionContext);
    const listView = useMemo(
        () => readInvoicesViewStateFromLocation(location.state),
        [location.state],
    );
    const queueKey = listView ? queueKeyFor(listView) : '';
    const queueRef = useRef<FrozenQueue | null>(null);
    const entryPageRef = useRef<number | null>(null);
    const seededKeyRef = useRef<string | null>(null);
    const listViewRef = useRef(listView);
    listViewRef.current = listView;
    const [queueTick, setQueueTick] = useState(0);
    const inflightPagesRef = useRef<Set<number>>(new Set());
    const failedPagesRef = useRef<Set<number>>(new Set());

    if (!listView) {
        entryPageRef.current = null;
    } else if (queueRef.current?.key !== queueKey) {
        queueRef.current = { key: queueKey, pages: {}, hasNext: {} };
        entryPageRef.current = listView.currentPage;
        inflightPagesRef.current = new Set();
        failedPagesRef.current = new Set();
    } else if (entryPageRef.current == null) {
        entryPageRef.current = listView.currentPage;
    }

    const organizationId = sessionContext?.organizationId ?? '';
    const sessionId = sessionContext?.sessionId ?? '';

    if (listView && queueRef.current && seededKeyRef.current !== queueKey) {
        seededKeyRef.current = queueKey;
        const entryPage = entryPageRef.current ?? listView.currentPage;
        const cached = queryClient.getQueryData<InvoiceFeedResponse>(invoicesFeedQueryKey({
            organizationId,
            type: listView.type,
            archivedFilter: listView.showArchived ? 'archived' : 'active',
            locationIds: listView.locationIds,
            page: entryPage,
            limit: INVOICES_LIST_FETCH_LIMIT,
            search: listView.debouncedSearch,
            additionalFilters: listView.additionalFilters,
        }));
        if (cached?.invoices && !queueRef.current.pages[entryPage]) {
            const snap = snapshotFeedPage(cached.invoices);
            queueRef.current.pages[entryPage] = snap.rows;
            queueRef.current.hasNext[entryPage] = snap.hasNext;
        }
    }

    useEffect(() => {
        const view = listViewRef.current;
        const queue = queueRef.current;
        if (!view || !sessionId || !queueKey || !queue || queue.key !== queueKey) return undefined;
        const key = queueKey;

        const requestPage = (pageNum: number) => {
            if (pageNum < 1) return;
            if (queue.pages[pageNum] || inflightPagesRef.current.has(pageNum) || failedPagesRef.current.has(pageNum)) {
                return;
            }
            inflightPagesRef.current.add(pageNum);
            void (async () => {
                try {
                    const queryKey = invoicesFeedQueryKey({
                        organizationId,
                        type: view.type,
                        archivedFilter: view.showArchived ? 'archived' : 'active',
                        locationIds: view.locationIds,
                        page: pageNum,
                        limit: INVOICES_LIST_FETCH_LIMIT,
                        search: view.debouncedSearch,
                        additionalFilters: view.additionalFilters,
                    });
                    const cached = queryClient.getQueryData<InvoiceFeedResponse>(queryKey);
                    const data = cached?.invoices
                        ? cached
                        : await fetchInvoicesFeedPage(sessionId, {
                            type: view.type,
                            archivedFilter: view.showArchived ? 'archived' : 'active',
                            locationIds: view.locationIds,
                            page: pageNum,
                            limit: INVOICES_LIST_FETCH_LIMIT,
                            search: view.debouncedSearch,
                            additionalFilters: view.additionalFilters,
                        });
                    const current = queueRef.current;
                    if (!current || current.key !== key || current.pages[pageNum]) return;
                    const snap = snapshotFeedPage(data.invoices);
                    current.pages[pageNum] = snap.rows;
                    current.hasNext[pageNum] = snap.hasNext;
                    setQueueTick((tick) => tick + 1);
                } catch {
                    failedPagesRef.current.add(pageNum);
                    setQueueTick((tick) => tick + 1);
                } finally {
                    inflightPagesRef.current.delete(pageNum);
                }
            })();
        };

        const entryPage = entryPageRef.current ?? view.currentPage;
        if (!queue.pages[entryPage]) {
            requestPage(entryPage);
            return undefined;
        }

        if (!invoiceId) return undefined;
        const position = neighborsFromFrozenPages(invoiceId, queue);
        if (!position.found || position.page == null) return undefined;
        // One page behind and one page ahead. Never the rest of the feed.
        if (position.page > 1) requestPage(position.page - 1);
        if (queue.hasNext[position.page]) requestPage(position.page + 1);
        return undefined;
    }, [queueKey, sessionId, organizationId, invoiceId, queueTick, queryClient]);

    const queue = queueRef.current?.key === queueKey ? queueRef.current : null;
    const resolved = invoiceId && queue
        ? neighborsFromFrozenPages(invoiceId, queue)
        : null;
    // queueTick retriggers this render after a page of the frozen queue arrives.
    void queueTick;

    const previousId = resolved?.previous?.id;
    const nextId = resolved?.next?.id;

    useEffect(() => {
        if (!sessionId) return;
        if (previousId) void prefetchInvoiceDetail(queryClient, sessionId, previousId);
        if (nextId) void prefetchInvoiceDetail(queryClient, sessionId, nextId);
    }, [previousId, nextId, sessionId, queryClient]);

    if (!listView || !invoiceId) return null;
    const entryPage = entryPageRef.current;
    const entryLoaded = entryPage != null && queue?.pages[entryPage] != null;
    const entryFailed = entryPage != null && failedPagesRef.current.has(entryPage);
    if ((entryLoaded || entryFailed) && !resolved?.found) return null;

    const go = (target: { id: string; page: number } | null) => {
        if (!target || target.id === invoiceId) return;
        const nextState: InvoicesViewStateCache = { ...listView, currentPage: target.page };
        saveInvoicesViewStateCache(nextState);
        navigate(`/invoices/${target.id}`, {
            state: {
                invoicesViewState: nextState,
                source: readDetailSource(location.state) ?? invoicesSidebarTabFromViewState(nextState),
            },
        });
    };

    const neighborPage = resolved?.page ?? null;
    const previousFailed = neighborPage != null && failedPagesRef.current.has(neighborPage - 1);
    const nextFailed = neighborPage != null && failedPagesRef.current.has(neighborPage + 1);
    const waitingForEntry = !entryLoaded && !entryFailed;
    const previousLoading = !previousFailed && (waitingForEntry || resolved?.awaitingPreviousPage === true);
    const nextLoading = !nextFailed && (waitingForEntry || resolved?.awaitingNextPage === true);

    return {
        onPrevious: () => go(resolved?.previous ?? null),
        onNext: () => go(resolved?.next ?? null),
        previousDisabled: !resolved?.previous,
        nextDisabled: !resolved?.next,
        previousLoading,
        nextLoading,
        previousLabel: invoiceNeighborTooltip('Previous', resolved?.previous ?? null),
        nextLabel: invoiceNeighborTooltip('Next', resolved?.next ?? null),
    };
}
